import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PlayerPassCard from "../components/PlayerPassCard";
import { API_BASE } from "../lib/api";
import { Download, Twitter, Linkedin, Copy, Check } from "lucide-react";

export default function PlayerPassPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (copied) { const t = setTimeout(() => setCopied(false), 1500); return () => clearTimeout(t); }
  }, [copied]);

  if (!user) return <div className="p-10 font-hand text-3xl">loading…</div>;

  const passParams = new URLSearchParams({
    name:             user.name || "Player",
    participant_id:   user.participant_id || "",
    goal_amount:      user.goal_amount || 0,
    strategy_summary: user.strategy_summary || "Building. Hustling. Shipping.",
    joined:           (user.created_at || "").slice(0, 10),
    avatar_url:       user.avatar_url || "",
  });
  const passUrl = `${API_BASE}/player-pass/${user.id}.pdf?${passParams}`;
  const profileUrl = `${window.location.origin}/u/${user.id}`;
  
  const shareText = `I just joined the #NoJobNoVC challenge — funding my business with grit, not VCs. 🚀\n\n🎯 Target: $${Number(user.goal_amount || 0).toLocaleString()}\n🔥 Strategy: ${user.strategy_summary || "Building in public."}\n\nJoin the movement and follow my journey:\n`;

  // Download PDF via fetch+blob so the browser never navigates away from the SPA
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(passUrl);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nojobnovc-pass-${user.participant_id || "player"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      // Fallback: open in new tab
      window.open(passUrl, "_blank");
    } finally {
      setDownloading(false);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '#NoJobNoVC Player Pass',
          text: shareText,
          url: profileUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      await navigator.clipboard.writeText(shareText + "\n" + profileUrl);
      setCopied(true);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      {isWelcome && (
        <div className="brutal-card bg-marker-yellow p-4 mb-6" data-testid="welcome-banner">
          <div className="font-hand text-2xl rotate-[-1deg]">— you're in —</div>
          <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight">
            Welcome to #NoJobNoVC, {user.name}
          </h2>
          <p>Your Player Pass is ready. Share it. Pin it. Wear it loud.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="flex justify-center">
          <PlayerPassCard
            name={user.name}
            participantId={user.participant_id}
            goal={user.goal_amount}
            joined={String(user.created_at || "").slice(0, 10)}
            rotate="rotate-[-2deg]"
          />
        </div>

        <div>
          <div className="font-hand text-marker-red text-3xl rotate-[-1deg]">— make it public —</div>
          <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight">Your Player Pass</h1>
          <p className="mt-3 leading-relaxed">
            Download the pass, then drop it on Twitter or LinkedIn — wherever you build in public.
            (Note: You'll need to manually attach the downloaded file to your post!)
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="brutal-btn brutal-btn-yellow"
              data-testid="pass-download-btn"
            >
              <Download size={16} /> {downloading ? "Downloading…" : "Download Pass"}
            </button>
            <button
              onClick={handleNativeShare}
              className="brutal-btn"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Share via Device"}
            </button>
            <a
              className="brutal-btn brutal-btn-blue"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(profileUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="pass-share-twitter"
            >
              <Twitter size={16} /> Tweet it
            </a>
            <a
              className="brutal-btn brutal-btn-red"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="pass-share-linkedin"
            >
              <Linkedin size={16} /> Share on LinkedIn
            </a>
          </div>

          <div className="mt-6 brutal-card-sm p-4 bg-cream">
            <div className="font-mono text-xs uppercase tracking-widest text-ink/60">Participant ID</div>
            <div className="font-mono font-bold text-xl">{user.participant_id}</div>
          </div>

          <div className="mt-6">
            <Link to="/me" className="brutal-btn">Back to my profile →</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
